import { create } from 'zustand'
import {
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  linkWithPopup,
  unlink,
  type User,
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { auth, googleProvider, facebookProvider, db, getStorageInstance, authFns } from '../firebase'
import { USER_ROLES, ROLE_PERMISSIONS, type UserRole } from '../constants/roles'
import { getErrorMessage, type FirestoreTimestamp } from '../types/firebase'

export interface RoleEntry {
  role: UserRole
  assignedAt: FirestoreTimestamp
  assignedBy: string
  orgId: string | null
  orgName: string | null
}

const EMPTY_ROLES: RoleEntry[] = []

// dev-only fallback identity; never a real production UID (rule 8)
const DEV_UID = (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_DEV_UID) || 'dev-local-user'

interface UserDocument {
  email: string
  displayName: string
  photoURL: string | null
  roles: RoleEntry[]
  role?: string
  contactInfo?: {
    name: string
    email: string
    phone: string
    relationship?: string
  }
  managedProfiles?: Array<{
    profileId: string
    ownerUid: string
    profileName: string
    role: string
  }>
  orgIds?: string[]
  country?: string
  timezone?: string
  orgApplicationStatus?: 'pending' | 'approved' | 'rejected'
  panicMode?: 'record' | 'stream'
  panicCameraFacing?: 'user' | 'environment'
  sosRecording?: boolean
  adminSecurity?: {
    commandCenterMfaReady?: boolean
    commandCenterVerifiedAt?: FirestoreTimestamp
  }
  uiPrefs?: {
    quickActions?: string[]
  }
  createdAt: FirestoreTimestamp
  updatedAt: FirestoreTimestamp
}

interface AuthState {
  user: User | null
  userDoc: UserDocument | null
  loading: boolean
  initialized: boolean

  // Derived getters (computed equivalents)
  isLoggedIn: () => boolean
  uid: () => string | null
  roles: () => RoleEntry[]
  primaryRole: () => UserRole
  isSuperAdmin: () => boolean
  permissions: () => string[]
  hasRole: (role: UserRole) => boolean
  hasPermission: (permission: string) => boolean

  // Actions
  init: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithGoogleIdToken: (idToken: string) => Promise<void>
  signInWithFacebook: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<void>
  resendVerificationEmail: () => Promise<void>
  sendPasswordReset: (email: string) => Promise<void>
  signOut: () => Promise<void>
  reloadUserDoc: () => Promise<void>
  updateUIPrefs: (prefs: NonNullable<UserDocument['uiPrefs']>) => Promise<void>
  uploadUserPhoto: (file: File) => Promise<void>
  linkWithGoogle: () => Promise<void>
  linkWithFacebook: () => Promise<void>
  unlinkProvider: (providerId: string) => Promise<void>
  devLogin: (as?: 'family' | 'org') => void
}

function detectRegion(): { country: string; timezone: string } {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Unknown'
  const locale = navigator.language ?? ''
  const parts = locale.split('-')
  const countryFromLocale = parts.length >= 2 ? parts[parts.length - 1].toUpperCase() : ''
  const tzCountryMap: Record<string, string> = {
    'Asia/Manila': 'PH',
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Denver': 'US',
    'America/Los_Angeles': 'US',
    'Europe/London': 'GB',
    'Europe/Paris': 'FR',
    'Europe/Berlin': 'DE',
    'Asia/Tokyo': 'JP',
    'Asia/Singapore': 'SG',
    'Asia/Kuala_Lumpur': 'MY',
    'Asia/Jakarta': 'ID',
    'Asia/Bangkok': 'TH',
    'Asia/Dubai': 'AE',
    'Asia/Kolkata': 'IN',
    'Australia/Sydney': 'AU',
    'Pacific/Auckland': 'NZ',
  }
  const country =
    countryFromLocale.length === 2 ? countryFromLocale : (tzCountryMap[timezone] ?? 'Unknown')
  return { country, timezone }
}

async function loadUserDocument(firebaseUser: User): Promise<UserDocument> {
  const docRef = doc(db, 'users', firebaseUser.uid)
  const snapshot = await getDoc(docRef)

  if (snapshot.exists()) {
    const data = snapshot.data() as UserDocument
    if (!data.country) {
      const { country, timezone } = detectRegion()
      updateDoc(docRef, { country, timezone }).catch((err: unknown) => {
        console.warn('[auth.loadUserDocument] non-critical:', err)
      })
      data.country = country
      data.timezone = timezone
    }
    if (!data.photoURL && firebaseUser.photoURL) {
      updateDoc(docRef, { photoURL: firebaseUser.photoURL }).catch((err: unknown) => {
        console.warn('[auth.loadUserDocument] photoURL backfill:', err)
      })
      data.photoURL = firebaseUser.photoURL
    }
    return data
  }

  const { country, timezone } = detectRegion()
  const newDoc: UserDocument = {
    email: firebaseUser.email ?? '',
    displayName: firebaseUser.displayName ?? '',
    photoURL: firebaseUser.photoURL,
    roles: [],
    contactInfo: {
      name: firebaseUser.displayName ?? '',
      email: firebaseUser.email ?? '',
      phone: '',
    },
    country,
    timezone,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
  await setDoc(docRef, newDoc)
  return newDoc
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  userDoc: null,
  loading: true,
  initialized: false,

  isLoggedIn: () => get().user !== null,
  uid: () => get().user?.uid ?? null,
  // Stable fallback — a fresh [] per call breaks useSyncExternalStore selectors (infinite loop when signed out)
  roles: () => get().userDoc?.roles ?? EMPTY_ROLES,
  primaryRole: () => {
    const roles = get().roles()
    if (roles.length > 0) {
      const orgRole = roles.find(
        (r) =>
          r.role === USER_ROLES.SUPER_ADMIN ||
          r.role === USER_ROLES.ORG_ADMIN ||
          r.role === USER_ROLES.STAFF,
      )
      if (orgRole) return orgRole.role
      return roles[0].role
    }
    const legacyRole = get().userDoc?.role
    if (legacyRole) return legacyRole as UserRole
    return USER_ROLES.FAMILY_ADMIN
  },
  isSuperAdmin: () => get().roles().some((r) => r.role === USER_ROLES.SUPER_ADMIN),
  permissions: () => {
    const perms = new Set<string>()
    for (const entry of get().roles()) {
      const rolePerms = ROLE_PERMISSIONS[entry.role] ?? []
      rolePerms.forEach((p) => perms.add(p))
    }
    return [...perms]
  },
  hasRole: (role) => get().roles().some((r) => r.role === role),
  hasPermission: (permission) => get().permissions().includes(permission),

  init: () =>
    new Promise((resolve, reject) => {
      const unsubscribe = authFns.onAuthStateChanged(
        auth,
        async (firebaseUser) => {
          try {
            if (firebaseUser) {
              const userDoc = await loadUserDocument(firebaseUser)
              set({ user: firebaseUser, userDoc, loading: false, initialized: true })
            } else {
              set({ user: null, userDoc: null, loading: false, initialized: true })
            }
            unsubscribe()
            resolve()
          } catch (err: unknown) {
            console.error('[auth.init] Error loading user doc:', getErrorMessage(err))
            set({ loading: false, initialized: true })
            unsubscribe()
            resolve()
          }
        },
        reject,
      )
    }),

  signInWithGoogle: async () => {
    const result = await signInWithPopup(auth, googleProvider)
    const userDoc = await loadUserDocument(result.user)
    set({ user: result.user, userDoc })
  },

  signInWithGoogleIdToken: async (idToken) => {
    const credential = GoogleAuthProvider.credential(idToken)
    const result = await signInWithCredential(auth, credential)
    const userDoc = await loadUserDocument(result.user)
    set({ user: result.user, userDoc })
  },

  signInWithFacebook: async () => {
    const result = await signInWithPopup(auth, facebookProvider)
    const userDoc = await loadUserDocument(result.user)
    set({ user: result.user, userDoc })
  },

  signInWithEmail: async (email, password) => {
    const result = await authFns.signInWithEmailAndPassword(auth, email, password)
    const userDoc = await loadUserDocument(result.user)
    set({ user: result.user, userDoc })
  },

  signUpWithEmail: async (email, password) => {
    const result = await authFns.createUserWithEmailAndPassword(auth, email, password)
    await authFns.sendEmailVerification(result.user)
    const userDoc = await loadUserDocument(result.user)
    set({ user: result.user, userDoc })
  },

  resendVerificationEmail: async () => {
    const { user } = get()
    if (!user) return
    await authFns.sendEmailVerification(user)
  },

  sendPasswordReset: async (email) => {
    await authFns.sendPasswordResetEmail(auth, email)
  },

  signOut: async () => {
    await authFns.signOut(auth)
    set({ user: null, userDoc: null })
  },

  reloadUserDoc: async () => {
    const { user } = get()
    if (!user) return
    const userDoc = await loadUserDocument(user)
    set({ userDoc })
  },

  updateUIPrefs: async (prefs) => {
    const uid = get().uid()
    if (!uid) return
    await updateDoc(doc(db, 'users', uid), { uiPrefs: prefs })
    set((s) => ({ userDoc: s.userDoc ? { ...s.userDoc, uiPrefs: prefs } : s.userDoc }))
  },

  uploadUserPhoto: async (file) => {
    const { user } = get()
    if (!user) throw new Error('Not authenticated')
    const path = `users/${user.uid}/avatar.jpg`
    const ref = storageRef(await getStorageInstance(), path)
    await uploadBytes(ref, file, { contentType: file.type || 'image/jpeg' })
    const url = await getDownloadURL(ref)
    await authFns.updateProfile(user, { photoURL: url })
    set({ user: { ...user, photoURL: url } as User })
    // Sync to Firestore so messaging + org staff views pick up new photo
    try {
      await updateDoc(doc(db, 'users', user.uid), { photoURL: url })
    } catch (err) {
      console.warn('[auth] uploadUserPhoto — users doc sync failed:', err)
    }
    const orgRole = get().roles().find(r => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)
    if (orgRole) {
      try {
        await updateDoc(doc(db, 'org_staff', user.uid), { photoURL: url })
      } catch (err) {
        console.warn('[auth] uploadUserPhoto — org_staff sync failed:', err)
      }
    }
  },

  linkWithGoogle: async () => {
    const { user } = get()
    if (!user) throw new Error('Not authenticated')
    await linkWithPopup(user, googleProvider)
    if (auth.currentUser) set({ user: auth.currentUser })
  },

  linkWithFacebook: async () => {
    const { user } = get()
    if (!user) throw new Error('Not authenticated')
    await linkWithPopup(user, facebookProvider)
    if (auth.currentUser) set({ user: auth.currentUser })
  },

  unlinkProvider: async (providerId: string) => {
    const { user } = get()
    if (!user) throw new Error('Not authenticated')
    await unlink(user, providerId)
    if (auth.currentUser) set({ user: auth.currentUser })
  },

  devLogin: (as = 'family') => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metaEnv = (import.meta as { env?: Record<string, string> }).env ?? {}
    if (!metaEnv.DEV) return
    const email = metaEnv.VITE_DEV_EMAIL
    const password = metaEnv.VITE_DEV_PASSWORD
    if (email && password) {
      authFns.signInWithEmailAndPassword(auth, email, password)
        .then(async (result) => {
          const userDoc = await loadUserDocument(result.user)
          if (as === 'org') {
            userDoc.roles = [{ role: USER_ROLES.ORG_ADMIN, assignedAt: null as unknown as FirestoreTimestamp, assignedBy: 'dev', orgId: 'dev-org', orgName: 'Sunshine Therapy Center' }]
            userDoc.orgIds = ['dev-org']
          }
          set({ user: result.user, userDoc })
        })
        .catch(() => {
          set({
            user: { uid: DEV_UID, displayName: 'Ron', email, photoURL: null, emailVerified: true } as unknown as User,
            userDoc: { email, displayName: 'Ron', photoURL: null, roles: [{ role: USER_ROLES.FAMILY_ADMIN, assignedAt: null, assignedBy: 'dev', orgId: null, orgName: null }], orgIds: [], createdAt: null, updatedAt: null },
          })
        })
      return
    }
    set({
      user: {
        uid: DEV_UID,
        displayName: 'Ron',
        email: 'rosilvosa@gmail.com',
        photoURL: null,
        emailVerified: true,
      } as unknown as User,
      userDoc: {
        email: 'rosilvosa@gmail.com',
        displayName: 'Ron',
        photoURL: null,
        roles:
          as === 'org'
            ? [{ role: USER_ROLES.ORG_ADMIN, assignedAt: null, assignedBy: 'dev', orgId: 'dev-org', orgName: 'Sunshine Therapy Center' }]
            : [{ role: USER_ROLES.FAMILY_ADMIN, assignedAt: null, assignedBy: 'dev', orgId: null, orgName: null }],
        orgIds: as === 'org' ? ['dev-org'] : [],
        createdAt: null,
        updatedAt: null,
      },
    })
  },
}))
